import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Row, createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import useGetTables from '@/services/lobby/queries/useGetTables';
import { TableInfo } from '@/services/table/tableTypes';
import TableInfoModal from '@/pages/table/components/TableInfoModal/TableInfoModal';
import { ReducerState } from '@/services/common/lib/reducerState';

import TableCardSimple from './TableCardSimple';
import LobbyStatusLoader from './LobbyStatusLoader';
import { twMerge } from 'tailwind-merge';

const columnHelper = createColumnHelper<TableInfo>();

const LobbyTableMobile = ({ containerClassName = '' }: { containerClassName?: string }) => {
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const filters = useSelector((state: ReducerState) => state.auth?.lobbyFilters);

  const [selectedRow, setSelectedRow] = useState<TableInfo | null>(null);
  const [showTableInfo, setShowTableInfo] = useState(false);

  const { data, isFetching, fetchNextPage, isRefetching } = useGetTables({
    filter: filters,
    sort: undefined, // we set it to undefined since we have no way of controlling sorting on mobile view (see figma) and in case this is set previously on the desktop view
    paginationOptions: { skip: 0, take: 20 },
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        enableSorting: false,
        header: 'Table Name',
        cell: ({ row }) => (
          <TableCardSimple
            key={row.id}
            table={row.original}
            setShowTableInfo={setShowTableInfo}
            setSelectedRow={setSelectedRow}
            statusPadding={false}
          />
        ),
      }),
    ],
    [],
  );

  const flatData = React.useMemo(() => data?.pages?.flatMap((page) => page.data.tables) ?? [], [data]);
  const totalDBRowCount = data?.pages[0].data.total ?? 0;
  const totalFetched = flatData.length;

  const tableInstance = useReactTable<TableInfo>({
    columns,
    data: flatData,
    getCoreRowModel: getCoreRowModel(),
  });

  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;

        if (scrollHeight - scrollTop - clientHeight < 300 && !isFetching && totalFetched < totalDBRowCount)
          fetchNextPage();
      }
    },
    [fetchNextPage, isFetching, totalFetched, totalDBRowCount],
  );

  React.useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);
  const { rows } = tableInstance.getRowModel();
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 120,
    getScrollElement: () => tableContainerRef.current,
    measureElement:
      typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });

  return (
    <div
      className={twMerge(
        // the max-h is calculated by subtracting all the top element heights
        'scrollbar-phenom relative max-h-[calc(100vh-64px-88px-32px-48px-16px)] w-full overflow-auto',
        containerClassName,
      )}
      ref={tableContainerRef}
      onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
    >
      <table className="grid w-full">
        <tbody
          className="relative grid"
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index] as Row<TableInfo>;
            return (
              <tr
                data-index={virtualRow.index}
                ref={(node) => rowVirtualizer.measureElement(node)}
                key={row.id}
                className="absolute flex w-full"
                style={{
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="my-1 flex w-full">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <LobbyStatusLoader isFetching={!isRefetching && isFetching} totalRowCount={totalDBRowCount} />

      {selectedRow && (
        <div className="bg-red-500">
          <TableInfoModal
            open={showTableInfo}
            setClose={() => setShowTableInfo(false)}
            maxPlayers={selectedRow.maxPlayers}
            currentPlayers={selectedRow.players}
            name={selectedRow.name}
            gameParams={selectedRow.gameParams}
            currentVariant={undefined}
            variants={selectedRow.variants}
          />
        </div>
      )}
    </div>
  );
};

export default LobbyTableMobile;
